import { Step, newMixedStep, RedirectStep } from '@/types/step';
import { createDataFromStep } from '@/utils/flow';
import { safeParse } from '@/utils/json';

import { Config } from '@/types/config';
import { AppointRequest, InitialRequest, Actions } from '@/types/requests';
import { setDefaultSettings } from '@/utils/url';
import { getStoredSession, deleteSession } from '@/utils/session';

type RequestCallback = (
    status: number,
    step: Step | null,
    errorMessage?: string
) => void;

export function makeRequest ({ data, method = 'POST', appConfig, callback }: {
    data: InitialRequest | AppointRequest;
    method?: 'GET' | 'POST';
    appConfig: Config;
    callback: RequestCallback;
}): XMLHttpRequest | undefined {
    if (data instanceof AppointRequest) {
        if (typeof (data.formAction) === 'string' && typeof (data.formMethod) === 'string' && data.answers) {
            let form = document.createElement('form');

            form.setAttribute('action', data.formAction);
            form.setAttribute('method', data.formMethod);

            data.answers.forEach(answer => {
                if (typeof (answer.formId) === 'string') {
                    let input = document.createElement('input');
                    input.setAttribute('type', 'hidden');
                    input.setAttribute('name', answer.formId);
                    input.setAttribute('value', answer.value);
                    form.appendChild(input);
                }
            });

            document.body.appendChild(form);
            form.submit();
            return;
        }
    }

    let storeHistory = getStoredSession(appConfig);
    if (storeHistory !== null) {
        let lastStoredStep = storeHistory[storeHistory.length - 1];
        deleteSession(appConfig.index);
        if (window.location.href.match(new RegExp(appConfig.storeSessionAutosubmitRegExp))) {
            lastStoredStep.formAction = null;
            if (lastStoredStep.setWindowUrl && !appConfig.useBrowserHistory) {
                window.history.replaceState(undefined, '', lastStoredStep.setWindowUrl);
                appConfig.initParams._embedingUrl = window.location.href;
            }

            makeRequest({
                data: createDataFromStep(Actions.ANSWER, lastStoredStep, appConfig),
                appConfig,
                callback: (status, step, errorMessage) => {
                    if (step !== null && storeHistory !== null) {
                        step.history = storeHistory;
                    }
                    callback(status, step, errorMessage);
                },
            });
            return;
        } else {
            lastStoredStep.history = storeHistory.slice(0, -1);
            callback(200, lastStoredStep);
            return;
        }
    }

    const xhr = new XMLHttpRequest();
    xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) {
            return;
        }

        if (xhr.status === 200 && appConfig.addParams) {
            appConfig.addParams = undefined;
        }

        let stepConstructor = xhr.responseText && safeParse(xhr.responseText);

        if (stepConstructor) {
            if (xhr.status === 200) {
                let step = (window.__odrLastResponse = newMixedStep({
                    ...stepConstructor,
                    data,
                }));

                if (step instanceof RedirectStep) {
                    window.location.href = step.to;
                } else {
                    callback(200, step);
                }
            } else {
                callback(xhr.status, null, stepConstructor.message);
            }
        } else {
            callback(xhr.status, null);
        }
    };

    xhr.open(method, `${appConfig.apiUrl}/appoint`);

    setDefaultSettings(xhr, appConfig);

    if (data instanceof AppointRequest && data.stepXhrTimeout) {
        xhr.timeout = data.stepXhrTimeout;
    }

    if (data instanceof AppointRequest && data.answers) {
        data.answers = data.answers.filter(question => !question.formId);
    }

    xhr.send(JSON.stringify(data));

    return xhr;
}

let __odrSessionRetryPause = 0;

export function getSession (
    appConfig: Config,
    index: number,
    callback?: RequestCallback,
) {
    let data = new InitialRequest({}, appConfig);
    makeRequest({
        data,
        appConfig,
        callback: function (status, step, _) {
            if (status === 200 && step !== null) {
                if (callback) {
                    callback(status, step);
                }
            } else {
                setTimeout(function () {
                    getSession(appConfig, index, callback);
                }, 1000 * __odrSessionRetryPause);
                __odrSessionRetryPause++;
            }
        },
    });
}
